import * as React from 'react';
import * as mui from '@mui/material';
import * as icons from '@mui/icons-material';

interface CharacterDialogButtonProps extends mui.BoxProps {
	index: number;
	selectedCharacter1?: string;
	isMale: boolean;
	selectedCharacter: string;
	setSelectedCharacter: (character: string) => void;
}

interface CharacterOptionsProps {
	character: string;
	selectedCharacter: string;
	setSelectedCharacter: (character: string) => void;
	setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

function CharacterOption(props: CharacterOptionsProps) {
	const { character, selectedCharacter, setSelectedCharacter, setModalOpen } = props;

	const handleOnClick = () => {
		setSelectedCharacter(character);
		setModalOpen(false);
	};

	return (
		<mui.Box
			sx={{
				backgroundImage: `url(https://storage.googleapis.com/faboola_wordpress_static/img/characters/${character}.png)`,
				width: '100%',
				maxWidth: '120px',
				pt: '100%',
				borderRadius: '100%',
				backgroundSize: '85%',
				backgroundPosition: 'bottom center',
				backgroundRepeat: 'no-repeat',
				textAlign: 'center',
				my: 2,
				mx: 'auto',
				cursor: 'pointer',
				border: theme => `2px solid ${character === selectedCharacter ? theme.palette.primary.main : theme.palette.divider}`,
				'&:hover': theme => ({
					border: `2px solid ${theme.palette.primary.main}`,
				}),
			}}
			onClick={handleOnClick}
		/>
	);
}

export default function CharacterDialogButton(props: CharacterDialogButtonProps) {
	const { index, isMale, selectedCharacter1, selectedCharacter, setSelectedCharacter, ...BoxProps } = props;

	const [modalOpen, setModalOpen] = React.useState(false);

	const kind1 = parseInt(selectedCharacter1?.charAt(0) ?? '1');

	const characters = (() => {
		if (index === 1) {
			return [1, 2, 3].flatMap(n => [1, 2, 3].map(m => `${n}_${isMale ? 'M' : 'F'}${m}`));
		} else if (index === 2) {
			const variant = [2, 1, 1][kind1 - 1];
			return [1, 2, 3].map(n => `${variant}_${isMale ? 'M' : 'F'}${n}`);
		} else if (index === 3) {
			const variant = [3, 3, 2][kind1 - 1];
			return [1, 2, 3].map(n => `${variant}_${isMale ? 'M' : 'F'}${n}`);
		}

		return [];
	})();

	const shouldHighlight =
		(selectedCharacter.charAt(2).toLowerCase() === 'm' && isMale) || (selectedCharacter.charAt(2).toLowerCase() === 'f' && !isMale);

	const handleModalOpen = () => {
		setModalOpen(true);
	};

	const handleModalClose = () => {
		setModalOpen(false);
	};

	return (
		<mui.Box {...BoxProps}>
			<mui.Tooltip
				title={isMale ? 'Personaggio maschile' : 'Personaggio femminile'}
				aria-label={isMale ? 'personaggio maschile' : 'personaggio femminile'}
			>
				<mui.Box
					component='button'
					onClick={handleModalOpen}
					sx={{
						width: '45px',
						height: '45px',
						borderRadius: '100%',
						backgroundImage: `url(https://storage.googleapis.com/faboola_wordpress_static/img/${isMale ? 'male' : 'female'}.svg)`,
						backgroundColor: isMale ? '#A3CCEE' : '#F4A7A8',
						backgroundRepeat: 'no-repeat',
						backgroundPosition: 'center',
						backgroundSize: '50%',
						border: `2px solid ${shouldHighlight ? 'white' : 'transparent'}`,
						cursor: 'pointer',
						'&:hover': {
							border: '2px solid white',
						},
					}}
				/>
			</mui.Tooltip>

			<mui.Dialog open={modalOpen} onClose={handleModalClose} aria-labelledby='character-dialog-title' maxWidth='md'>
				<mui.DialogTitle>
					<mui.Typography variant='h6'>Scegli l'aspetto che preferisci!</mui.Typography>
					<mui.IconButton aria-label='close' onClick={handleModalClose}>
						<icons.Close />
					</mui.IconButton>
				</mui.DialogTitle>

				<mui.DialogContent>
					<mui.Grid container spacing={2}>
						{characters.map(character => (
							<mui.Grid item xs={4}>
								<CharacterOption
									character={character}
									selectedCharacter={selectedCharacter}
									setSelectedCharacter={setSelectedCharacter}
									setModalOpen={setModalOpen}
								/>
							</mui.Grid>
						))}
					</mui.Grid>
				</mui.DialogContent>
			</mui.Dialog>
		</mui.Box>
	);
}
